import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';

function Repos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localRepos, setLocalRepos] = useState([]);
  const [localReposLoading, setLocalReposLoading] = useState(true);
  const [directName, setDirectName] = useState('');
  const navigate = useNavigate();

  const localFilter = searchParams.get('repos_search') || '';
  const setLocalFilter = (val) =>
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('repos_search', val);
      return n;
    });

  useEffect(() => {
    let isMounted = true;
    setLocalReposLoading(true);
    axios.get(`${config.BASE_URL}/repositories/local/details`)
      .then(r => {
        if (!isMounted) return;
        setLocalRepos(r.data || []);
        setLocalReposLoading(false);
      })
      .catch(async () => {
        try {
          const fallback = await axios.get(`${config.BASE_URL}/repositories/local`);
          if (!isMounted) return;
          setLocalRepos(
            (fallback.data || []).map(name => ({
              name, languages: [], managers: [], manifests: [], projectCount: 0, hasMonorepo: false,
            }))
          );
        } catch {
          if (!isMounted) return;
          setLocalRepos([]);
        } finally {
          if (isMounted) setLocalReposLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, []);

  const handleDirectOpen = (e) => {
    e.preventDefault();
    const name = directName.trim();
    if (name) navigate('/RepoDetails/' + name);
  };

  const filteredLocalRepos = localFilter
    ? localRepos.filter(r => r.name.toLowerCase().includes(localFilter.toLowerCase()))
    : localRepos;

  return (
    <div className="App-body">

      {/* Page header */}
      <div className="ce-dashboard-header" style={{ marginBottom: '1.5rem' }}>
        <div>
          <h1 className="ce-page-title">📦 Projects</h1>
          <p className="ce-page-subtitle">
            Your software supply chain security hub — one project per application, service, or component.
          </p>
        </div>
        <form className="d-flex gap-2" onSubmit={handleDirectOpen}>
          <input
            className="ce-search-input"
            type="text"
            placeholder="Open project by name…"
            value={directName}
            onChange={e => setDirectName(e.target.value)}
          />
          <button className="ce-btn ce-btn--primary ce-btn--sm text-nowrap" type="submit" disabled={!directName.trim()}>
            Open →
          </button>
          <button
            type="button"
            className="ce-btn ce-btn--secondary ce-btn--sm text-nowrap"
            onClick={() => navigate('/upload')}
          >
            🐳 IaC / Container
          </button>
        </form>
      </div>

      {/* What is a Project — info banner */}
      <div className="repos-info-banner mb-4">
        <div className="repos-info-banner__body">
          <div className="repos-info-banner__text">
            <span className="repos-info-banner__lead">Each project gives you full visibility into your supply chain:</span>
            <span className="repos-info-banner__sub">Upload an SBOM or connect a repository — DepGuard does the rest automatically.</span>
          </div>
          <div className="repos-info-banner__chips">
            <span className="repos-info-chip repos-info-chip--red">🔒 Vulnerabilities</span>
            <span className="repos-info-chip repos-info-chip--blue">📋 SBOM</span>
            <span className="repos-info-chip repos-info-chip--purple">⚖️ EU CRA Compliance</span>
            <span className="repos-info-chip repos-info-chip--green">📦 Package Health</span>
            <span className="repos-info-chip repos-info-chip--orange">🔬 SAST Analysis</span>
            <span className="repos-info-chip repos-info-chip--grey">📈 Trend History</span>
            <span className="repos-info-chip repos-info-chip--iac">🐳 Docker / Helm / Terraform</span>
          </div>
        </div>
      </div>

      {/* Repository list */}
      {localReposLoading ? (
        <section>
          <div className="ce-section-header mb-3">
            <span className="ce-section-title">Repositories</span>
          </div>
          <div className="ce-repo-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="ce-repo-card" style={{ cursor: 'default' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div className="sk-base" style={{ width: '55%', height: 15, borderRadius: 5 }} />
                  <div className="sk-base" style={{ width: 48, height: 15, borderRadius: 100 }} />
                </div>
                <div className="sk-base" style={{ width: '30%', height: 11, borderRadius: 4, marginBottom: '0.75rem' }} />
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  {[45, 55, 40].map((w, j) => (
                    <div key={j} className="sk-base" style={{ width: w, height: 20, borderRadius: 100 }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : localRepos.length > 0 ? (
        <section>
          <div className="ce-section-header mb-3">
            <span className="ce-section-title">
              {localRepos.length} {localRepos.length === 1 ? 'project' : 'projects'}
            </span>
            {localRepos.length > 5 && (
              <input
                className="ce-search-input"
                placeholder="Filter projects…"
                value={localFilter}
                onChange={e => setLocalFilter(e.target.value)}
              />
            )}
          </div>

          <div className="ce-repo-grid">
            {filteredLocalRepos.map((repo) => (
              <div
                key={repo.name}
                className="ce-repo-card"
                onClick={() => navigate('/RepoDetails/' + repo.name)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.375rem' }}>
                  <div className="ce-repo-name" style={{ maxWidth: '80%' }}>{repo.name}</div>
                  {repo.hasMonorepo && (
                    <span className="ce-tag" style={{ flexShrink: 0 }}>
                      Monorepo
                    </span>
                  )}
                </div>

                <div className="ce-repo-meta" style={{ marginBottom: '0.625rem' }}>
                  {repo.projectCount > 0
                    ? `${repo.projectCount} project${repo.projectCount === 1 ? '' : 's'} detected`
                    : 'Click to open & upload an SBOM'}
                </div>

                {((repo.languages || []).length > 0 || (repo.managers || []).length > 0) && (
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    {(repo.languages || []).slice(0, 3).map(lang => (
                      <span key={lang} className="ce-tag">
                        {lang}
                      </span>
                    ))}
                    {(repo.managers || []).slice(0, 2).map(mgr => (
                      <span key={mgr} className="ce-tag">
                        {mgr}
                      </span>
                    ))}
                  </div>
                )}

                {(repo.manifests || []).length > 0 && (
                  <div className="ce-repo-meta">
                    {repo.manifests.slice(0, 3).join(' · ')}
                  </div>
                )}

                {repo.detectionError && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--bs-warning, #f59e0b)', marginTop: '0.25rem' }}>
                    ⚠ {repo.detectionError}
                  </div>
                )}
              </div>
            ))}
          </div>

          {localFilter && filteredLocalRepos.length === 0 && (
            <p className="text-secondary small mt-3">No projects match "{localFilter}".</p>
          )}
        </section>
      ) : (
        <EmptyState
          icon="📦"
          title="No projects yet"
          description="Upload an SBOM to create your first project, or use the open-by-name field above to connect an existing repository."
          action={
            <div className="d-flex gap-2 flex-wrap justify-content-center">
              <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={() => navigate('/upload')}>
                ⬆️ Upload an SBOM
              </button>
              <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => navigate('/upload')}>
                🐳 Scan Docker / IaC
              </button>
            </div>
          }
        />
      )}
    </div>
  );
}

export default Repos;
